/**
 * EmailCaptureDialog — E-Mail-Nacherfassung im Kundenportal (08/2026).
 *
 * Kunden aus dem SMS-OTP-Login haben eine Platzhalter-Adresse
 * (<nummer>@phone.strongfit.ch). Dieser Dialog fragt bei jedem Login
 * (einmal pro Browser-Session, wegklickbar) nach der echten E-Mail und
 * verifiziert sie per 6-stelligem Code.
 * Backend: POST /api/auth/email-capture/request + /verify
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Mail, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PHONE_PLACEHOLDER_DOMAIN = '@phone.strongfit.ch';

export const needsEmailCapture = (customer) =>
  (customer?.email || '').toLowerCase().endsWith(PHONE_PLACEHOLDER_DOMAIN);

const EmailCaptureDialog = ({ open, onClose, token, onSaved }) => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const requestCode = async () => {
    const trimmed = email.trim();
    if (!trimmed.includes('@') || !trimmed.includes('.')) {
      setError('Bitte gib eine gültige E-Mail-Adresse an.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/auth/email-capture/request`, { email: trimmed }, authHeaders);
      setCodeSent(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Etwas hat nicht geklappt. Bitte versuche es erneut.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/auth/email-capture/verify`,
        { email: email.trim(), code: code.trim() },
        authHeaders
      );
      toast.success('E-Mail-Adresse gespeichert');
      onSaved?.(res.data?.email || email.trim());
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Code ungültig oder abgelaufen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent
        className="sm:max-w-md bg-[#141310] border-[#2a2822] text-white"
        data-testid="email-capture-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            {codeSent ? <ShieldCheck className="w-5 h-5 text-[#c9a34e]" /> : <Mail className="w-5 h-5 text-[#c9a34e]" />}
            Deine E-Mail-Adresse
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {codeSent
              ? `Wir haben dir einen 6-stelligen Code an ${email.trim()} geschickt. Gib ihn hier ein:`
              : 'Damit du Terminbestätigungen und Belege erhältst, brauchen wir noch deine E-Mail-Adresse.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {!codeSent ? (
            <Input
              type="email"
              autoComplete="email"
              placeholder="name@beispiel.ch"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && requestCode()}
              className="bg-[#0a0a0a] border-[#2a2822] text-white placeholder:text-gray-500"
              data-testid="email-capture-email-input"
            />
          ) : (
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="••••••"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && verifyCode()}
              className="bg-[#0a0a0a] border-[#2a2822] text-white text-center text-xl tracking-[8px] placeholder:text-gray-500"
              data-testid="email-capture-code-input"
            />
          )}
          {error && (
            <p className="text-red-400 text-sm" data-testid="email-capture-error">{error}</p>
          )}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            className="text-gray-400 hover:text-white hover:bg-[#1c1a15]"
            data-testid="email-capture-later-btn"
          >
            Später
          </Button>
          <div className="flex gap-2">
            {codeSent && (
              <Button
                variant="outline"
                onClick={requestCode}
                disabled={busy}
                className="border-[#2a2822] text-gray-300 hover:bg-[#1c1a15]"
                data-testid="email-capture-resend-btn"
              >
                Code erneut senden
              </Button>
            )}
            <Button
              onClick={codeSent ? verifyCode : requestCode}
              disabled={busy || (codeSent ? code.length !== 6 : !email.trim())}
              className="bg-[#c9a34e] hover:bg-[#b89220] text-black font-semibold"
              data-testid="email-capture-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : codeSent ? 'Bestätigen' : 'Code senden'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailCaptureDialog;
